import type { SnEvent } from './types'

/** The `event_type` values the API sends. */
export const EVENT_OWNER_TYPE = {
  TEAM: 'team',
  COMPETITION: 'competition',
} as const

/**
 * The two kinds of event.
 *
 * Deliberately the SAME strings the API's `event_type` uses, so the resolver
 * can hand the value straight through once it recognizes it. Kept as its own
 * type rather than aliased to the payload field, because the resolver must
 * still answer for a response that omits `event_type` entirely.
 */
export type EventOwnerType = 'team' | 'competition'

/**
 * Whether an event belongs to a TEAM or to a COMPETITION.
 *
 * Reads the API's `event_type` — the authoritative discriminator, now sent on
 * every event response.
 *
 * This used to fall back to "does it carry a `schedule`?" for responses that
 * predated the field. That fallback is gone: it could not tell a team event
 * apart from a competition one on any endpoint whose payload nests the
 * schedule differently, and keeping two sources of truth meant the edit modal
 * and the detail screen could disagree about the same event.
 *
 * An UNRECOGNIZED value (null, or a kind added later) resolves to 'team',
 * which is the conservative answer: it hides competition-only affordances
 * rather than offering a competition scope the event may not have.
 */
export const resolveEventOwnerType = (
  // Only the field it actually reads, rather than a whole `SnEvent`: the list
  // row hands it a narrowed `Pick`, and demanding the full shape there would
  // force a cast at the call site.
  event?: Pick<SnEvent, 'event_type'> | null
): EventOwnerType =>
  event?.event_type === EVENT_OWNER_TYPE.COMPETITION ? 'competition' : 'team'

/** Convenience predicate for the common "is this a competition event?" check. */
export const isCompetitionEvent = (
  event?: Pick<SnEvent, 'event_type'> | null
): boolean => resolveEventOwnerType(event) === 'competition'

/**
 * The competition to scope the form's option lists to (`/venues`,
 * `/schedules/:id/teams/simple`), or undefined for a team-owned event.
 *
 * `root_schedule` — the TOP-LEVEL competition — is preferred over `schedule`,
 * which may be a division: both endpoints are scoped to the competition owning
 * the pool, and a division id returns the wrong one (see
 * `web/src/features/game/gameListView/utils/editModalScheduleId.ts`).
 * `schedule` is the fallback for responses that omit the root.
 */
export const resolveEventScheduleId = (
  event?: SnEvent | null
): string | undefined => {
  if (!isCompetitionEvent(event)) {
    return undefined
  }
  return event?.root_schedule?.id ?? event?.schedule?.id ?? undefined
}

interface ResolveEventFormScheduleIdArgs {
  /** The event being EDITED. Null/undefined means the form is in add mode. */
  event?: SnEvent | null
  /**
   * The surface's own competition scope, used only for ADD — the competition
   * page's `SCHEDULE_ID`, or nothing on a team page or the detail page.
   */
  hostScheduleId?: string | null
}

/**
 * The competition scope the add/edit event form should run under.
 *
 * **On EDIT the answer comes from the EVENT, never from the host surface.**
 * That is the whole point: a competition list and a team list can each hold
 * both kinds of event — the two payloads differ only in `schedule`, sharing a
 * `team` AND a `root_schedule` — so the page an event was opened from says
 * nothing about which kind it is.
 *
 * Taking the host's scope instead is not merely cosmetic. It feeds
 * `buildEventPayload`'s `hasTeamField`, so editing a TEAM-owned event from a
 * competition page would show the Team select and let a save send
 * `team_id: null`, unlinking the event from the team it was created under. The
 * reverse — a competition event edited from a team page — hides the Location
 * and Team pickers and offers an address block for a venue-backed event.
 *
 * On ADD there is no event to ask, so the host's own scope is the answer: the
 * competition page creates schedule-owned events, a team page creates
 * team-owned ones (and passes none).
 *
 * The games side learned this the same way — see
 * `web/src/features/game/gameListView/utils/editModalScheduleId.ts`, which
 * resolves the mirror-image problem for the Game modal.
 */
export const resolveEventFormScheduleId = ({
  event,
  hostScheduleId,
}: ResolveEventFormScheduleIdArgs): string | undefined =>
  event ? resolveEventScheduleId(event) : hostScheduleId || undefined

/** The competition an event sits in, ready to render as a link. */
export interface EventCompetitionRef {
  id: string
  name: string
}

/**
 * The competition to NAME on the detail screen, or null when there is none.
 *
 * COMPETITION EVENTS ONLY. Gated on `event_type` rather than on the presence
 * of a schedule, because a TEAM event still reports the organization's
 * `root_schedule` — reading the fields alone would name a competition on every
 * team event.
 *
 * Within a competition event the name comes from `schedule`, which every
 * competition event carries. `root_schedule` is the fallback, for a response
 * that sends the root but no `schedule`.
 *
 * This is deliberately the OPPOSITE order to `resolveEventScheduleId` above,
 * and the two are not interchangeable. That one feeds competition-scoped
 * lookups (`/venues`, `/schedules/:id/teams/simple`) where a division id
 * returns the wrong list, so it must climb to the root. This one only labels
 * the event, and the label users expect is the schedule the event was created
 * under — not the top-level competition it happens to roll up to.
 *
 * Also null for a competition event whose payload carries no usable id/name
 * pair — the caller renders nothing rather than an empty link.
 */
export const resolveEventCompetition = (
  event?: Pick<SnEvent, 'event_type' | 'root_schedule' | 'schedule'> | null
): EventCompetitionRef | null => {
  if (resolveEventOwnerType(event) !== 'competition') {
    return null
  }

  const candidates = [event?.schedule, event?.root_schedule]
  for (const candidate of candidates) {
    const id = candidate?.id
    // `name_full` is what some responses send instead of `name`; the
    // breadcrumb helper reads both for the same reason.
    const name =
      candidate?.name?.trim() ||
      (
        candidate as { name_full?: string | null } | null | undefined
      )?.name_full?.trim()
    if (id && name) {
      return { id, name }
    }
  }
  return null
}
